import React from 'react';
import { X, Info } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { MODE_THEME, MODE_ICON, type ModeId } from '../shared/modeTheme';
import { CenteredModal } from '../shared/CenteredModal';
import { MODE_INTRO_CONTENT } from './modeIntroContent';
interface ModeIntroSheetProps {
  isOpen: boolean;
  modeId: ModeId;
  onStart: () => void;
  onCancel: () => void;
  /** Only used by fluency to show the cold-start banner */
  isColdStart?: boolean;
}
/**
 * ModeIntroSheet — Shared intro modal for all practice modes.
 * Replaces the 4 separate intro modals (FluencyIntro, SprintIntro, etc.)
 * Part of the Learning Lab v1.3 Wave C structural refactor (F-CODE-1).
 */
export function ModeIntroSheet({
  isOpen,
  modeId,
  onStart,
  onCancel,
  isColdStart
}: ModeIntroSheetProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const theme = MODE_THEME[modeId];
  const content = MODE_INTRO_CONTENT[modeId];
  const HeaderIcon = MODE_ICON[modeId];
  return <CenteredModal isOpen={isOpen} onClose={onCancel} maxWidth="md" ariaLabel="Uitleg over deze oefenvorm" data-id="element-3137" data-mp="226njtur701vt">
      
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.gradient} p-6 text-white text-center relative`} data-id="element-3138" data-mp="1x6j261ejt140">
        
        <button onClick={onCancel} className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors" aria-label="Sluiten" data-id="element-3139" data-mp="2b60wsxeiz8la">
          
          <X className="w-5 h-5" data-id="element-3140" data-mp="2kwn84ff7lcy6" />
        </button>
        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md" data-id="element-3141" data-mp="2fybz26wqn6h0">
          <HeaderIcon className={`w-8 h-8 text-white ${modeId === 'fluency' ? 'fill-white' : ''}`} data-id="element-3142" data-mp="2uwe3ejblfvzb" />
          
        </div>
        <h2 className="text-2xl font-black tracking-tight" data-id="element-3143" data-mp="2pxt2sisfjaac">{theme.title}</h2>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8" data-id="element-3144" data-mp="2x1998pl2pnlr">
        <div className="flex gap-4 items-start mb-6" data-id="element-3145" data-mp="31y9hn75eb2g1">
          <div className="flex-shrink-0 mt-1" data-id="element-3146" data-mp="31ysptw58ucgv">
            <DjayAvatar size="md" emotion="excited" variant={selectedVariant} animate={true} data-id="element-3147" data-mp="36x3za5rmmkyk" />
            
          </div>
          <div className={`${theme.bgSoft} rounded-2xl rounded-tl-none p-4 border ${theme.borderSoft} ${theme.textDark} text-sm font-medium leading-relaxed`} data-id="element-3148" data-mp="3bpdvu4n8yj9t">
            
            {content.djayMessage}
          </div>
        </div>

        {/* Cold-start info banner (Fluency only) */}
        {isColdStart && modeId === 'fluency' && <div className="flex items-start gap-3 p-3 mb-6 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-3149" data-mp="3powqpvr1ry46">
            <div className="mt-0.5 bg-amber-100 p-1 rounded-full text-amber-600 flex-shrink-0" data-id="element-3150" data-mp="3ul3zdwatvnpt">
              <Info className="w-4 h-4" data-id="element-3151" data-mp="1vc6z5ko5j0zy" />
            </div>
            <p className="text-xs text-amber-800 font-medium leading-relaxed" data-id="element-3152" data-mp="20a8hg3dr6wad">
              Snel & Zeker werkt het beste als je al wat geoefend hebt. Nu krijg
              je een goede startset om mee te beginnen!
            </p>
          </div>}

        <div className="space-y-4 mb-8" data-id="element-3153" data-mp="1nx3cfecghmtb">
          {content.features.map((feature, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-3154" data-mp="1j04l13k6ftyt" data-mpi={idx}>
              <div className={`mt-0.5 p-1 rounded-full ${feature.iconBgClass} ${feature.iconColorClass}`} data-id="element-3155" data-mp="1e1jll6cr1ebf">
              
                <feature.icon className="w-4 h-4" data-id="element-3156" data-mp="2bo0elzeg95rd" />
              </div>
              <div data-id="element-3157" data-mp="26p5yx6bs3xbh">
                <p className="text-sm font-bold text-slate-800" data-id="element-3158" data-mp="26qqriqej966p">
                  {feature.title}
                </p>
                <p className="text-xs text-slate-500" data-id="element-3159" data-mp="21ohy5sxr0242">{feature.description}</p>
              </div>
            </div>)}
        </div>

        <div className="flex gap-3" data-id="element-3160" data-mp="qb60y0whuhqv">
          <Button variant="secondary" onClick={() => {
          soundManager.playClick();
          hapticManager.hapticLight();
          onCancel(); // F-FLOW-2: "Niet nu" dismisses without starting
        }} className="flex-1" data-id="element-3161" data-mp="qc8nxxqp401i">
            
            Niet nu
          </Button>
          <Button variant="primary" onClick={() => {
          soundManager.playNavigate?.() || soundManager.playClick();
          hapticManager.hapticMedium();
          onStart();
        }} className={`flex-1 bg-gradient-to-r ${theme.gradient} hover:brightness-110 border-none shadow-lg ${theme.shadow}`} data-id="element-3162" data-mp="2ex8otaripl2d">
            
            {content.primaryCta}
          </Button>
        </div>
      </div>
    </CenteredModal>;
}